import { Card, Grid, Paper, Typography, styled, useTheme } from '@mui/material'
import { Padding } from '../../styles/paddingComponent'
import React, { ReactElement, useMemo } from 'react'
import { padding } from 'src/styles/paddingCss'
import {
  GridCallbackDetails,
  GridColumns,
  GridEnrichedColDef,
  GridEventListener,
  GridRowId,
  GridRowParams,
  GridValidRowModel,
} from '@mui/x-data-grid-pro'
import { useDialogCtx } from 'src/hooks/context-hooks'
import { ArrowRightRounded } from '@mui/icons-material'
import { DialogContextType } from 'src/enums'
interface ITableDataCard {
  row: any
  columns: GridColumns[] | GridEnrichedColDef<any, any, any>[]
  mobileViewCard?: (row: any) => ReactElement
  getDetailPanelContent?: ((params: GridRowParams<any>) => React.ReactNode) | undefined
  onRowClick: GridEventListener<'rowClick'> | undefined
  onCellClick?: GridEventListener<'cellClick'> | undefined
  contextType?: DialogContextType
  getRowId?: (row: GridValidRowModel) => GridRowId
}

const DetailsPanel = ({ content }) => {
  return <>{content}</>
}

const TableDataCard: React.FC<ITableDataCard> = ({
  row,
  columns,
  getDetailPanelContent,
  onRowClick,
  onCellClick,
  mobileViewCard,
  contextType,
  getRowId
}) => {
  const { palette } = useTheme()
  const { showDialog } = useDialogCtx()
  const action: any = useMemo(() => {
    if (columns) {
      return (columns as any[]).find((c: any) => ['actions'].includes(c.type))
    }
    return null
  }, [columns])
  // First non-action column used to emulate a cell click for tables that navigate via onCellClick
  // (which only fires per-cell on desktop). Tapping the whole card maps to that cell on mobile.
  const primaryColumn: any = useMemo(
    () => (columns as any[]).find((c: any) => !['actions', 'detailPanelToggle'].includes(c.type)),
    [columns],
  )
  const rowId = getRowId && getRowId != undefined ? getRowId(row) : row.id
  const isRowClickable = !!onRowClick || !!onCellClick || !!getDetailPanelContent
  const handleCardClick = (e: React.MouseEvent) => {
    const rowParams = {
      row: row as GridRowParams<any>,
      id: rowId,
      columns: columns as GridEnrichedColDef<any, any, any>[],
      getValue: () => {},
    }
    // Desktop fires both cell- and row-click, but several tables wire the same navigation handler to
    // both, so prefer the cell handler (the one carrying the per-cell guard) to avoid double-firing.
    if (onCellClick && primaryColumn) {
      onCellClick(
        { ...rowParams, field: primaryColumn.field, value: row[primaryColumn.field], colDef: primaryColumn } as any,
        e as any,
        {} as GridCallbackDetails<any>,
      )
    } else if (onRowClick) {
      onRowClick(rowParams, e as any, {} as GridCallbackDetails<any>)
    } else if (getDetailPanelContent) {
      onSelect()
    }
  }
  const onSelect = () => {
    if (getDetailPanelContent)
      showDialog(
        DetailsPanel,
        {
          componentProps: {
            content: getDetailPanelContent({
              row: row as GridRowParams<any>,
              id: getRowId && getRowId != undefined ? getRowId(row) : row.id ,
              columns: columns as GridEnrichedColDef<any, any, any>[],
              getValue: () => {},
            }),
          },
        },
        contextType,
      )
  }

  return (
    <Padding.pb8>
      {mobileViewCard ? (
        mobileViewCard(row)
      ) : (
        <Paper
          component={RowCard}
          sx={{ cursor: isRowClickable ? 'pointer' : 'default' }}
          onClick={handleCardClick}
        >
          <Grid container>
            <GridRow item xs={12} justifyContent={'flex-end'}>
              {action != null ? (action.renderCell ? action.renderCell({ row: row, id: getRowId && getRowId != undefined  ? getRowId(row) : row.id }) : row[action.field]) : undefined}
              {(columns as any[]).find((c: any) => ['detailPanelToggle'].includes(c.type)) && (
                <ArrowRightRounded
                  onClick={e => {
                    e.stopPropagation()
                    onSelect()
                  }}
                />
              )}
            </GridRow>

            <Grid container>
              {columns.map(column => {
                return (
                  <React.Fragment key={column.field}>
                    {!['actions', 'detailPanelToggle'].includes(column.type) && (
                      <>
                        <GridRow item xs={6}>
                          <Typography fontWeight={600} variant="subtitle1" color={palette.blueGray[palette.mode]}>
                            {column.headerName}
                          </Typography>
                        </GridRow>
                        <GridRow item xs={6}>
                          <Typography fontWeight={'medium'} variant="subtitle1">
                            {column.renderCell
                              ? column.renderCell({ row: row, value: row[column.field],id: getRowId && getRowId != undefined  ? getRowId(row) : row.id })
                              : column.valueGetter
                              ? column.valueGetter({ row, value: row[column.field] })
                              : row[column.field] ?? '-'}
                          </Typography>
                        </GridRow>
                      </>
                    )}
                  </React.Fragment>
                )
              })}
            </Grid>
          </Grid>
        </Paper>
      )}
    </Padding.pb8>
  )
}

export default TableDataCard

const RowCard = styled(Card)`
  ${padding.p16};
`

const GridRow = styled(Grid)`
  display: flex;
  flex-direction: row;
  ${padding.p8};
`
